"use client";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";

const data = [
  { name: "Myanmar", "2016-2023": 1.5, "2023-2030": 19.0 },
  { name: "Cambodia", "2016-2023": 7.8, "2023-2030": 16.5 },
  { name: "Viet Nam", "2016-2023": 6.2, "2023-2030": 10.8 },
  { name: "Philippines", "2016-2023": 3.8, "2023-2030": 6.8 },
  { name: "Laos", "2016-2023": 7.1, "2023-2030": 5.8 },
  { name: "Thailand", "2016-2023": 1.5, "2023-2030": 3.5 },
  { name: "ASEAN", "2016-2023": 3.8, "2023-2030": 3.2 },
  { name: "World", "2016-2023": 2.5, "2023-2030": 3.5 },
  { name: "Indonesia", "2016-2023": 4.5, "2023-2030": 3.2 },
  { name: "Singapore", "2016-2023": 1.5, "2023-2030": 2.8 },
  { name: "Malaysia", "2016-2023": 2.6, "2023-2030": 2.8 },
  { name: "Brunei Darussalam", "2016-2023": 4.5, "2023-2030": 1.8 },
];

const CustomLegend = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="flex items-center gap-6 my-2">
      <div className="flex items-center gap-2">
        <div className="w-5 h-4 bg-[#A5D6F2]"></div>
        <span className="text-[#333]" style={{ fontSize: isMobile ? 11 : 13 }}>2016-2023</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-5 h-4 bg-[#264069]"></div>
        <span className="text-[#333]" style={{ fontSize: isMobile ? 11 : 13 }}>2023-2030</span>
      </div>
    </div>
  );
};

export default function GrowthRateChart({ isActive }: { isActive: boolean }) {
  const [show, setShow] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => setShow(true), 200);
    } else {
      setShow(false);
    }

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1170);
      setIsSmallMobile(width < 480);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isActive]);

  return (
    <div
      className="w-full flex flex-col bg-[#F7F7F7] p-6 font-sans overflow-hidden"
      style={{ height: isMobile ? (isSmallMobile ? 480 : 520) : "100%" }}
    >
      <div className="w-full max-w-[800px] h-full flex flex-col mx-auto relative">
        <h2 className="text-[#333] font-medium mb-2" style={{ fontSize: isMobile ? 14 : 16 }}>
          Annual growth rate* (%)
        </h2>
        <CustomLegend isMobile={isMobile} />

        <div className="flex-1 w-full relative min-h-[300px]">
          {show && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{
                  top: 20,
                  right: isMobile ? 15 : 30,
                  left: isSmallMobile ? 40 : isMobile ? 65 : 85,
                  bottom: 20
                }}
                barCategoryGap={isSmallMobile ? 4 : isMobile ? 6 : 10}
              >
                <defs>
                  <pattern
                    id="diagonalHatch"
                    width="8"
                    height="8"
                    patternTransform="rotate(45 0 0)"
                    patternUnits="userSpaceOnUse"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="8"
                      stroke="#D1D5DB"
                      strokeWidth="2"
                    />
                  </pattern>
                </defs>
                <CartesianGrid horizontal={false} stroke="#D1D5DB" />
                <XAxis
                  type="number"
                  domain={[0, 20]}
                  ticks={[0, 5, 10, 15, 20]}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={isSmallMobile ? 70 : isMobile ? 95 : 105}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "#9CA3AF",
                    fontSize: isSmallMobile ? 10 : isMobile ? 11 : 13
                  }}
                  reversed
                />

                {/* ASEAN & World background highlight */}
                {/* Extending y1 and y2 a bit if it was continuous, but for categorical we just pass the names */}
                <ReferenceArea
                  y1="ASEAN"
                  y2="World"
                  fill="url(#diagonalHatch)"
                />

                <Bar
                  dataKey="2016-2023"
                  fill="#A5D6F2"
                  barSize={isSmallMobile ? 6 : isMobile ? 8 : 12}
                  isAnimationActive={true}
                />
                <Bar
                  dataKey="2023-2030"
                  fill="#264069"
                  barSize={isSmallMobile ? 6 : isMobile ? 8 : 12}
                  isAnimationActive={true}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
